import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, ShoppingBag, Heart, Star, Sparkles, Award, Shield, ChevronRight } from 'lucide-react'
import { useCart } from '../context/CartContext'
import { useAuth } from '../context/AuthContext'
import { productsAPI, reviewsAPI } from '../services/api'
import toast from 'react-hot-toast'

const ProductDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { addToCart } = useCart()
  const { user } = useAuth()
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [reviews, setReviews] = useState([])
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' })
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    setLoading(true)
    productsAPI.getById(id)
      .then(res => setProduct(res.data))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false))
  }, [id])

  const fetchReviews = () => {
    reviewsAPI.getByProduct(id)
      .then(res => setReviews(res.data))
      .catch(() => setReviews([]))
  }

  useEffect(() => {
    fetchReviews()
  }, [id])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [id])

  const handleSubmitReview = async (e) => {
    e.preventDefault()
    if (!user) {
      toast.error('Please login to write a review')
      navigate('/login')
      return
    }
    if (!newReview.comment.trim()) {
      toast.error('Please write a comment')
      return
    }
    setSubmitting(true)
    try {
      await reviewsAPI.create({
        product_id: id,
        rating: newReview.rating,
        comment: newReview.comment
      })
      toast.success('Review submitted successfully!')
      setNewReview({ rating: 5, comment: '' })
      fetchReviews()
    } catch (err) {
      // error handled by interceptor
    } finally {
      setSubmitting(false)
    }
  }

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600 text-lg">Loading product...</p>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-serif font-bold text-gray-800 mb-4">Product not found</h2>
          <Link to="/shop" className="text-champagne-600 hover:text-champagne-700">
            Return to Shop
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="312:4" data-component-id="client/src/pages/ProductDetail.jsx:312:4">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="314:6" data-component-id="client/src/pages/ProductDetail.jsx:314:6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="315:8" data-component-id="client/src/pages/ProductDetail.jsx:315:8">
          <div className="flex items-center gap-2 text-sm" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="316:10" data-component-id="client/src/pages/ProductDetail.jsx:316:10">
            <Link to="/" className="text-gray-600 hover:text-champagne-600" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="317:12" data-component-id="client/src/pages/ProductDetail.jsx:317:12">Home</Link>
            <ChevronRight className="w-4 h-4 text-gray-400" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="318:12" data-component-id="client/src/pages/ProductDetail.jsx:318:12" />
            <Link to="/shop" className="text-gray-600 hover:text-champagne-600" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="319:12" data-component-id="client/src/pages/ProductDetail.jsx:319:12">Shop</Link>
            <ChevronRight className="w-4 h-4 text-gray-400" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="320:12" data-component-id="client/src/pages/ProductDetail.jsx:320:12" />
            <span className="text-gray-800 font-medium" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="321:12" data-component-id="client/src/pages/ProductDetail.jsx:321:12">{product.name}</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="326:6" data-component-id="client/src/pages/ProductDetail.jsx:326:6">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-champagne-600 mb-8 transition-colors"
          data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="328:10" data-component-id="client/src/pages/ProductDetail.jsx:328:10"
        >
          <ArrowLeft className="w-5 h-5" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="332:10" data-component-id="client/src/pages/ProductDetail.jsx:332:10" />
          Back to Shop
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="336:8" data-component-id="client/src/pages/ProductDetail.jsx:336:8">
          {/* Product Image */}
          <div className="relative" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="338:10" data-component-id="client/src/pages/ProductDetail.jsx:338:10">
            <div className="aspect-square rounded-2xl overflow-hidden bg-white shadow-lg" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="339:12" data-component-id="client/src/pages/ProductDetail.jsx:339:12">
              <img 
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'https://images.pexels.com/photos/1454171/pexels-photo-1454171.jpeg?auto=compress&cs=tinysrgb&w=1200'
                }}
                data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="340:14" data-component-id="client/src/pages/ProductDetail.jsx:340:14"
              />
            </div>
            {product.badge && (
              <span className="absolute top-4 left-4 bg-champagne-600 text-white text-sm font-medium px-4 py-2 rounded-full shadow-lg" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="350:14" data-component-id="client/src/pages/ProductDetail.jsx:350:14">
                {product.badge}
              </span>
            )}
          </div>

          {/* Product Info */}
          <div data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="357:10" data-component-id="client/src/pages/ProductDetail.jsx:357:10">
            <div className="mb-4" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="358:12" data-component-id="client/src/pages/ProductDetail.jsx:358:12">
              <span className="text-sm text-champagne-600 font-medium" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="359:14" data-component-id="client/src/pages/ProductDetail.jsx:359:14">{product.category}</span>
              <h1 className="font-serif text-4xl md:text-5xl font-bold text-gray-800 mt-2 mb-4" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="360:14" data-component-id="client/src/pages/ProductDetail.jsx:360:14">
                {product.name}
              </h1>
              
              {/* Rating */}
              <div className="flex items-center gap-3 mb-6" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="365:14" data-component-id="client/src/pages/ProductDetail.jsx:365:14">
                <div className="flex items-center gap-1" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="366:16" data-component-id="client/src/pages/ProductDetail.jsx:366:16">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={`w-5 h-5 ${i < Math.floor(averageRating) ? 'fill-champagne-500 text-champagne-500' : 'text-gray-300'}`}
                      data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="368:20" data-component-id="client/src/pages/ProductDetail.jsx:368:20"
                    />
                  ))}
                </div>
                <span className="text-gray-600" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="374:16" data-component-id="client/src/pages/ProductDetail.jsx:374:16">
                  {averageRating} ({reviews.length} {reviews.length === 1 ? 'review' : 'reviews'})
                </span>
              </div>
            </div>

            <p className="text-gray-700 text-lg mb-6 leading-relaxed" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="380:12" data-component-id="client/src/pages/ProductDetail.jsx:380:12">
              {product.description}
            </p>

            {/* Price */}
            <div className="mb-8" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="385:12" data-component-id="client/src/pages/ProductDetail.jsx:385:12">
              <div className="flex items-baseline gap-3" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="386:14" data-component-id="client/src/pages/ProductDetail.jsx:386:14">
                <span className="text-4xl font-bold text-champagne-600" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="387:16" data-component-id="client/src/pages/ProductDetail.jsx:387:16">
                  ₹{product.price.toLocaleString()}
                </span>
                {(product.originalPrice || product.original_price) && (
                  <span className="text-xl text-gray-400 line-through" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="391:18" data-component-id="client/src/pages/ProductDetail.jsx:391:18">
                    ₹{(product.originalPrice || product.original_price).toLocaleString()}
                  </span>
                )}
              </div>
            </div>

            {/* Material Badge */}
            {product.material && (
              <div className="mb-6" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="400:14" data-component-id="client/src/pages/ProductDetail.jsx:400:14">
                <span className="inline-flex items-center gap-2 bg-champagne-50 text-champagne-700 px-4 py-2 rounded-full font-medium" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="401:16" data-component-id="client/src/pages/ProductDetail.jsx:401:16">
                  <Sparkles className="w-4 h-4" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="402:18" data-component-id="client/src/pages/ProductDetail.jsx:402:18" />
                  {product.material}
                </span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4 mb-8" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="409:12" data-component-id="client/src/pages/ProductDetail.jsx:409:12">
              <button 
                onClick={() => addToCart(product)}
                className="flex-1 bg-champagne-600 text-white h-14 rounded-full hover:bg-champagne-700 transition-all duration-200 shadow-lg hover:shadow-xl font-medium text-lg flex items-center justify-center gap-2"
                data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="410:14" data-component-id="client/src/pages/ProductDetail.jsx:410:14"
              >
                <ShoppingBag className="w-5 h-5" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="414:16" data-component-id="client/src/pages/ProductDetail.jsx:414:16" />
                Add to Cart
              </button>
              <button className="w-14 h-14 border-2 border-gray-300 rounded-full flex items-center justify-center hover:border-champagne-600 hover:text-champagne-600 transition-colors" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="417:14" data-component-id="client/src/pages/ProductDetail.jsx:417:14">
                <Heart className="w-6 h-6" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="418:16" data-component-id="client/src/pages/ProductDetail.jsx:418:16" />
              </button>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 py-6 border-t border-gray-200" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="423:12" data-component-id="client/src/pages/ProductDetail.jsx:423:12">
              <div className="text-center" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="424:14" data-component-id="client/src/pages/ProductDetail.jsx:424:14">
                <Award className="w-8 h-8 text-champagne-600 mx-auto mb-2" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="425:16" data-component-id="client/src/pages/ProductDetail.jsx:425:16" />
                <p className="text-xs text-gray-600 font-medium" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="426:16" data-component-id="client/src/pages/ProductDetail.jsx:426:16">Premium Quality</p>
              </div>
              <div className="text-center" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="428:14" data-component-id="client/src/pages/ProductDetail.jsx:428:14">
                <Shield className="w-8 h-8 text-champagne-600 mx-auto mb-2" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="429:16" data-component-id="client/src/pages/ProductDetail.jsx:429:16" />
                <p className="text-xs text-gray-600 font-medium" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="430:16" data-component-id="client/src/pages/ProductDetail.jsx:430:16">Certified Authentic</p>
              </div>
              <div className="text-center" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="432:14" data-component-id="client/src/pages/ProductDetail.jsx:432:14">
                <Sparkles className="w-8 h-8 text-champagne-600 mx-auto mb-2" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="433:16" data-component-id="client/src/pages/ProductDetail.jsx:433:16" />
                <p className="text-xs text-gray-600 font-medium" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="434:16" data-component-id="client/src/pages/ProductDetail.jsx:434:16">Handcrafted</p>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details / Why Special */}
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-16">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-gray-800 mb-8 text-center">
            Product Details
          </h2>
          
          {product.product_details && (
            <p className="text-gray-700 text-lg leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              {product.product_details}
            </p>
          )}

          {product.whySpecial && (
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {product.whySpecial.map((reason, index) => (
              <div key={index} className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-champagne-100 rounded-full flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-champagne-600" />
                </div>
                <p className="text-gray-700 leading-relaxed">{reason}</p>
              </div>
            ))}
          </div>
          )}

          {/* Slots Info */}
          {(product.available_slots !== undefined || product.total_slots !== undefined) && (
            <div className="flex items-center gap-4 mb-8 justify-center">
              <span className="text-sm font-medium text-gray-700">
                Available Slots: <span className="text-champagne-600 font-bold">{product.available_slots}</span> / {product.total_slots}
              </span>
            </div>
          )}

          {/* Specifications */}
          {product.specifications && (
          <div className="border-t border-gray-200 pt-8">
            <h3 className="font-serif text-2xl font-bold text-gray-800 mb-6">Product Specifications</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {Object.entries(product.specifications).map(([key, value]) => (
                <div key={key} className="flex justify-between py-3 border-b border-gray-100">
                  <span className="font-medium text-gray-700">{key}:</span>
                  <span className="text-gray-600">{value}</span>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>

        {/* Reviews Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="472:8" data-component-id="client/src/pages/ProductDetail.jsx:472:8">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-gray-800 mb-8" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="473:10" data-component-id="client/src/pages/ProductDetail.jsx:473:10">
            Customer Reviews
          </h2>

          {/* Review Summary */}
          <div className="flex items-center gap-8 mb-12 pb-8 border-b border-gray-200" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="478:10" data-component-id="client/src/pages/ProductDetail.jsx:478:10">
            <div className="text-center" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="479:12" data-component-id="client/src/pages/ProductDetail.jsx:479:12">
              <div className="text-5xl font-bold text-champagne-600 mb-2" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="480:14" data-component-id="client/src/pages/ProductDetail.jsx:480:14">{averageRating}</div>
              <div className="flex gap-1 mb-2" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="481:14" data-component-id="client/src/pages/ProductDetail.jsx:481:14">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className={`w-5 h-5 ${i < Math.floor(averageRating) ? 'fill-champagne-500 text-champagne-500' : 'text-gray-300'}`}
                    data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="483:18" data-component-id="client/src/pages/ProductDetail.jsx:483:18"
                  />
                ))}
              </div>
              <p className="text-sm text-gray-600" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="489:14" data-component-id="client/src/pages/ProductDetail.jsx:489:14">{reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}</p>
            </div>
          </div>

          {/* Reviews List */}
          <div className="space-y-6 mb-12" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="494:10" data-component-id="client/src/pages/ProductDetail.jsx:494:10">
            {reviews.map(review => (
              <div key={review.id} className="border-b border-gray-100 pb-6 last:border-0" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="496:14" data-component-id="client/src/pages/ProductDetail.jsx:496:14">
                <div className="flex items-start justify-between mb-3" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="497:16" data-component-id="client/src/pages/ProductDetail.jsx:497:16">
                  <div data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="498:18" data-component-id="client/src/pages/ProductDetail.jsx:498:18">
                    <div className="flex items-center gap-2 mb-2" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="499:20" data-component-id="client/src/pages/ProductDetail.jsx:499:20">
                      <h4 className="font-semibold text-gray-800" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="500:22" data-component-id="client/src/pages/ProductDetail.jsx:500:22">{review.user_display_name || review.user_name || 'Anonymous'}</h4>
                      {review.verified && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="502:24" data-component-id="client/src/pages/ProductDetail.jsx:502:24">
                          Verified Purchase
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1 mb-2" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="507:20" data-component-id="client/src/pages/ProductDetail.jsx:507:20">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`w-4 h-4 ${i < review.rating ? 'fill-champagne-500 text-champagne-500' : 'text-gray-300'}`}
                          data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="509:24" data-component-id="client/src/pages/ProductDetail.jsx:509:24"
                        />
                      ))}
                    </div>
                  </div>
                  <span className="text-sm text-gray-500" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="516:18" data-component-id="client/src/pages/ProductDetail.jsx:516:18">{review.created_at ? new Date(review.created_at).toLocaleDateString() : ''}</span>
                </div>
                <p className="text-gray-700 leading-relaxed" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="518:16" data-component-id="client/src/pages/ProductDetail.jsx:518:16">{review.comment}</p>
              </div>
            ))}
          </div>

          {/* Add Review Form */}
          <div className="bg-gray-50 rounded-xl p-6" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="524:10" data-component-id="client/src/pages/ProductDetail.jsx:524:10">
            <h3 className="font-serif text-2xl font-bold text-gray-800 mb-6" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="525:12" data-component-id="client/src/pages/ProductDetail.jsx:525:12">Write a Review</h3>
            {!user ? (
              <div className="text-center py-4">
                <p className="text-gray-600 mb-4">Please login to write a review</p>
                <Link to="/login" className="inline-flex items-center justify-center bg-champagne-600 text-white px-6 py-2.5 rounded-lg hover:bg-champagne-700 transition-colors font-medium">
                  Login to Review
                </Link>
              </div>
            ) : (
            <form onSubmit={handleSubmitReview} className="space-y-4" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="526:12" data-component-id="client/src/pages/ProductDetail.jsx:526:12">
              <div>
                <p className="text-sm text-gray-600 mb-4">Reviewing as <span className="font-semibold text-gray-800">{user.full_name}</span></p>
              </div>

              <div data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="541:14" data-component-id="client/src/pages/ProductDetail.jsx:541:14">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="542:16" data-component-id="client/src/pages/ProductDetail.jsx:542:16">
                  Rating
                </label>
                <div className="flex gap-2" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="545:16" data-component-id="client/src/pages/ProductDetail.jsx:545:16">
                  {[1, 2, 3, 4, 5].map(rating => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setNewReview({...newReview, rating})}
                      className="focus:outline-none"
                      data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="547:20" data-component-id="client/src/pages/ProductDetail.jsx:547:20"
                    >
                      <Star 
                        className={`w-8 h-8 ${rating <= newReview.rating ? 'fill-champagne-500 text-champagne-500' : 'text-gray-300'} hover:text-champagne-400 transition-colors`}
                        data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="554:22" data-component-id="client/src/pages/ProductDetail.jsx:554:22"
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="562:14" data-component-id="client/src/pages/ProductDetail.jsx:562:14">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="563:16" data-component-id="client/src/pages/ProductDetail.jsx:563:16">
                  Your Review
                </label>
                <textarea 
                  value={newReview.comment}
                  onChange={(e) => setNewReview({...newReview, comment: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-champagne-500 focus:border-champagne-500"
                  rows="4"
                  placeholder="Share your experience with this product..."
                  required
                  data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="566:16" data-component-id="client/src/pages/ProductDetail.jsx:566:16"
                ></textarea>
              </div>

              <button 
                type="submit"
                disabled={submitting}
                className="w-full bg-champagne-600 text-white h-12 rounded-lg hover:bg-champagne-700 transition-colors font-medium disabled:opacity-50"
                data-component-path="client/src/pages/ProductDetail.jsx" data-component-line="577:14" data-component-id="client/src/pages/ProductDetail.jsx:577:14"
              >
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </form>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductDetail
